export type DateSearchTimeZone = "utc" | "local";

export type ParsedDateSearch = {
	month: number;
	day: number;
	year?: number;
};

const DATE_SEARCH_PATTERN = /^(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2}|\d{4}))?$/;

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * Parses user-entered date text (`M/D`, `MM/DD`, `M/D/YY`, `MM/DD/YY`,
 * `M/D/YYYY`, `MM/DD/YYYY`) into its parts. Two-digit years map to 20YY.
 * Returns null when the text is not a complete, valid date variation.
 */
export const parseDateSearch = (input: string): ParsedDateSearch | null => {
	const match = DATE_SEARCH_PATTERN.exec(input.trim());
	if (!match) return null;

	const month = Number(match[1]);
	const day = Number(match[2]);
	if (month < 1 || month > 12 || day < 1 || day > 31) return null;

	if (match[3] === undefined) return { month, day };

	const rawYear = Number(match[3]);
	return { month, day, year: match[3].length === 2 ? 2000 + rawYear : rawYear };
};

const toDate = (value: Date | string | null | undefined): Date | null => {
	if (value == null || value === "") return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const getParts = (
	date: Date,
	timeZone: DateSearchTimeZone,
): { month: number; day: number; year: number } =>
	timeZone === "utc"
		? {
				month: date.getUTCMonth() + 1,
				day: date.getUTCDate(),
				year: date.getUTCFullYear(),
			}
		: {
				month: date.getMonth() + 1,
				day: date.getDate(),
				year: date.getFullYear(),
			};

/**
 * Case-insensitive date search. A complete date variation matches by month and
 * day (and year when entered); anything else falls back to a substring match
 * against the displayed `MM/dd/yyyy` text so partial typing keeps working.
 * Missing values and sentinel dates (year <= 1900) never match.
 */
export const dateMatchesSearch = (
	value: Date | string | null | undefined,
	input: string,
	timeZone: DateSearchTimeZone = "utc",
): boolean => {
	const trimmed = input.trim();
	if (!trimmed) return true;

	const date = toDate(value);
	if (!date) return false;

	const { month, day, year } = getParts(date, timeZone);
	if (year <= 1900) return false;

	const parsed = parseDateSearch(trimmed);
	if (parsed) {
		return (
			parsed.month === month &&
			parsed.day === day &&
			(parsed.year === undefined || parsed.year === year)
		);
	}

	return `${pad(month)}/${pad(day)}/${year}`.includes(trimmed);
};
